import {EventInput} from "@fullcalendar/vue3";
import dayjs from "@/utils/dayjs";
import Formats from "@/utils/UIConstants/Formats.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import Translator from "@/utils/common/Translator.ts";
import NaMaConfirmationDialog from "@/utils/forms/NaMaConfirmationDialog.ts";
import CriteriaBuilder from "@/utils/criteria/CriteriaBuilder.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import PlaceUtils from "@/utils/common/PlaceUtils.ts";
import EditPlace from "@/utils/presenters/EditPlace.ts";
import ViewMode from "@/utils/constants/ViewMode.ts";
import TechnicianAppointmentStatus from "@/utils/constants/TechnicianAppointmentStatus.ts";

type EntityReferenceData = namaTypes.EntityReferenceData;

export interface AppointmentStatusStyle {
  status: string;
  className: string;
  color: string;
}

/** A `field = value` criteria condition, or `field in values` when the value is a list. */
export type AppointmentCondition = [string, string | string[]];

export interface FilterRule {
  field: string;
  negate: boolean;
  value: string | string[];
}

export interface CalendarDateRange {
  start: Date | string;
  end: Date | string
}

/** Common ground between a plain `EventInput` and FullCalendar's live `EventApi`. */
export interface PeriodCandidate {
  id?: string;
  start?: any;
  end?: any;
  extendedProps?: any;
}

export default class TechnicianAppointmentUtils {

  static readonly APPOINTMENT_ENTITY = "TechnicianAppointment";
  static readonly CREW_ENTITY = "TechnicianCrew";
  static readonly UNAVAILABILITY_ENTITY = "TechnicianUnavailability";
  static readonly DEPARTMENT_ENTITY = "DepartmentSection";
  static readonly PROCEDURE_ENTITY = "TechnicianProcedure";

  static readonly DEPARTMENT_FIELD_ID = "departmentSection";
  static readonly PROCEDURE_FIELD_ID = "technicianProcedure";
  static readonly FROM_DOC_FIELD_ID = "fromDoc";

  static readonly DEFAULT_CREW_COLOR = "#64748b";

  static readonly COLOR_PALETTE = [
    "#2563eb", "#16a34a", "#db2777", "#d97706", "#7c3aed",
    "#0891b2", "#dc2626", "#4d7c0f", "#9333ea", "#0d9488"
  ];

  static readonly EMPTY_BOOKING_CALENDAR: namaTypes.DTOBookingCalendar = {rules: [], businessHours: []};

  static refLabel(ref?: EntityReferenceData | null): string {
    if (!ref)
      return "";
    return Translator.getNameForTitle(ref) || "";
  }

  static readonly STATUS_STYLES: AppointmentStatusStyle[] = [
    {status: TechnicianAppointmentStatus.Booked, className: "my-appt--booked", color: "#1d6fd1"},
    {status: TechnicianAppointmentStatus.Executed, className: "my-appt--executed", color: "#0f8a53"},
    {status: TechnicianAppointmentStatus.NoShow, className: "my-appt--no-show", color: "#d97706"},
    {status: TechnicianAppointmentStatus.Rescheduled, className: "my-appt--rescheduled", color: "#8b5cf6"},
    {status: TechnicianAppointmentStatus.Cancelled, className: "my-appt--cancelled", color: "#98a2b3"}
  ];

  static colorFor(crew: EntityReferenceData | undefined, colorMap: Map<string, string>): string {
    if (crew?.colorCode)
      return crew.colorCode
    const crewId = crew?.id
    if (!crewId)
      return TechnicianAppointmentUtils.DEFAULT_CREW_COLOR;
    if (!colorMap.has(crewId))
      colorMap.set(crewId, TechnicianAppointmentUtils.COLOR_PALETTE[colorMap.size % TechnicianAppointmentUtils.COLOR_PALETTE.length]);
    return colorMap.get(crewId)!;
  }

  // The server reads a java Date only in this text form, so the typed Date fields are sent as text
  static encodeDay(d: any): any {
    return dayjs(d).format("YYYY-MM-DD [00:00:00.000]");
  }

  static encodeTime(d: any): string {
    return dayjs(d).format("[1970-01-01] HH:mm:ss.SSS");
  }

  static encodeDateTime(d: any): any {
    return dayjs(d).format(Formats.DATE_MOMENT_SERVER_FORMAT);
  }

  static timeOfDay(time?: Date): string | undefined {
    if (ObjectChecker.isNotEmptyOrNull(time))
      return dayjs(time).format("HH:mm:ss");
    else
      return undefined;
  }

  static toCalendarBusinessHours(hours?: namaTypes.DTOBusinessHours[]): any {
    if (!hours?.length)
      return false;
    return hours.map(h => ({
      daysOfWeek: h.daysOfWeek,
      startTime: TechnicianAppointmentUtils.timeOfDay(h.startTime),
      endTime: TechnicianAppointmentUtils.timeOfDay(h.endTime)
    }));
  }

  static toCalendarValidRange(range?: namaTypes.DTOTimeRange | null): { start?: Date, end?: Date } | undefined {
    if (!range)
      return undefined;
    return {
      start: range.start ? dayjs(range.start).toDate() : undefined,
      end: range.end ? dayjs(range.end).toDate() : undefined
    };
  }

  static criteriaForField(fieldId?: string, deptId?: string | null): namaTypes.DTOCriteria | undefined {
    if (fieldId === TechnicianAppointmentUtils.DEPARTMENT_FIELD_ID)
      return CriteriaBuilder.create().openBracket().field("showInAppointmentsScreen").equal(true).closeBracket().build();
    if (fieldId === TechnicianAppointmentUtils.PROCEDURE_FIELD_ID && ObjectChecker.isNotEmptyOrNull(deptId))
      return CriteriaBuilder.create().openBracket().field("departmentSections.departmentSection.id").equal(deptId).or().field("departmentSections.departmentSection.id").isNull()
        .closeBracket().build();
    return undefined;
  }

  private static async postToTechAppointments<T>(url: string, data: any): Promise<T | undefined> {
    return await NamaCommon.namaFetch(url, {data, noLoadingIndicator: true}) as T | undefined;
  }

  static async loadCrews(departmentSectionId?: string | null, technicianProcedureId?: string | null): Promise<namaTypes.DTOTechnicianCrewInfo[]> {
    const request: namaTypes.ListBookableCrewsRequest = {
      departmentSectionId: departmentSectionId ?? undefined,
      technicianProcedureId: technicianProcedureId ?? undefined
    };
    const crews = await TechnicianAppointmentUtils.postToTechAppointments<namaTypes.DTOTechnicianCrewInfo[]>(NamaURLS.ListBookableCrews, request);
    return crews ?? [];
  }

  static async fetchMyAppointmentsScope(): Promise<namaTypes.DTOMyAppointmentsScope | null> {
    return await TechnicianAppointmentUtils.postToTechAppointments<namaTypes.DTOMyAppointmentsScope>(NamaURLS.FetchMyAppointmentsScope, {}) ?? null;
  }

  static async fetchBookingCalendar(departmentSectionIds: string[]): Promise<namaTypes.DTOBookingCalendar> {
    if (!departmentSectionIds.length)
      return TechnicianAppointmentUtils.EMPTY_BOOKING_CALENDAR;
    const request: namaTypes.BookingCalendarRequest = {departmentSectionIds};
    const calendar = await TechnicianAppointmentUtils.postToTechAppointments<namaTypes.DTOBookingCalendar>(NamaURLS.FetchBookingCalendar, request);
    return calendar ?? TechnicianAppointmentUtils.EMPTY_BOOKING_CALENDAR;
  }

  static async loadCalendarEvents(crewIds: string[], range: CalendarDateRange | null, includeCancelled: boolean,
                                  criteria?: namaTypes.DTOCriteria): Promise<Required<namaTypes.DTOCalendarEvents>> {
    if (!crewIds.length || !range?.start || !range?.end)
      return {periods: [], blocks: []};
    const request: namaTypes.CalendarEventsRequest = {
      crewIds,
      from: TechnicianAppointmentUtils.encodeDay(range.start),
      to: TechnicianAppointmentUtils.encodeDay(range.end),
      includeCancelled,
      criteria
    };
    const events = await TechnicianAppointmentUtils.postToTechAppointments<namaTypes.DTOCalendarEvents>(NamaURLS.ListCalendarEvents, request);
    return {periods: events?.periods ?? [], blocks: events?.blocks ?? []};
  }

  static async rescheduleAppointments(changes: namaTypes.DTOAppointmentChange[]): Promise<namaTypes.DTOAppointmentChangeResult[]> {
    if (!changes.length)
      return [];
    const results = await TechnicianAppointmentUtils.postChanges(changes);
    const retries = await TechnicianAppointmentUtils.confirmedRetries(changes, results);
    if (!retries.length)
      return results;
    const retried = new Map((await TechnicianAppointmentUtils.postChanges(retries)).map(r => [r.appointmentId, r]));
    return results.map(r => retried.get(r.appointmentId) ?? r);
  }

  private static async postChanges(changes: namaTypes.DTOAppointmentChange[]): Promise<namaTypes.DTOAppointmentChangeResult[]> {
    const request: namaTypes.RescheduleAppointmentsRequest = {changes};
    const results = await NamaCommon.namaFetch(NamaURLS.RescheduleAppointments, {data: request}) as namaTypes.DTOAppointmentChangeResult[];
    return Array.isArray(results) ? results : [];
  }

  // Same flow as the record screen: ask each confirmation the save raised, then re-post the change with the answers
  private static async confirmedRetries(changes: namaTypes.DTOAppointmentChange[],
                                        results: namaTypes.DTOAppointmentChangeResult[]): Promise<namaTypes.DTOAppointmentChange[]> {
    const retries: namaTypes.DTOAppointmentChange[] = [];
    for (const result of results) {
      const confirmations = result.result?.confirmations;
      const change = changes.find(c => c.appointmentId === result.appointmentId);
      if (result.saved || !confirmations?.length || !change)
        continue;
      for (const confirmation of confirmations)
        confirmation.confirmed = await NaMaConfirmationDialog.yesNoConfirmationDialog(Translator.translateNaMaText(confirmation.message), true,
          Translator.translateId('confirmBeforeStarting'));
      if (confirmations.every(c => c.confirmed))
        retries.push({...change, confirmations});
    }
    return retries;
  }

  static filterCriteria(conditions: AppointmentCondition[] = [], rules: FilterRule[] = [], matchAny = false): namaTypes.DTOCriteria | undefined {
    if (!conditions.length && !rules.length)
      return undefined;
    let expr = CriteriaBuilder.create().commitedBefore();
    for (const [field, value] of conditions)
      expr = Array.isArray(value) ? expr.and().field(field).in(...value) : expr.and().field(field).equal(value);
    if (rules.length) {
      expr = expr.and().openBracket();
      rules.forEach((rule, index) => {
        if (index)
          expr = matchAny ? expr.or() : expr.and();
        if (rule.negate) {
          // a blank field is "not that value" too, and SQL <> never matches NULL
          const inner = expr.openBracket().field(rule.field);
          const negated = Array.isArray(rule.value) ? inner.notIn(...rule.value) : inner.notEqual(rule.value);
          expr = negated.or().field(rule.field).isNull().closeBracket();
        } else {
          const ruleExpr = expr.field(rule.field);
          expr = Array.isArray(rule.value) ? ruleExpr.in(...rule.value) : ruleExpr.equal(rule.value);
        }
      });
      expr = expr.closeBracket();
    }
    return expr.build();
  }

  static busyEventFor(period: namaTypes.DTOCrewPeriod, colorMap: Map<string, string>): EventInput {
    const title = TechnicianAppointmentUtils.periodTitle(period);
    const status = period.status;
    const statusMarker = period.locked ? "🔒 " : "";
    const flagged = ObjectChecker.isNotEmptyOrNull(status) && status !== TechnicianAppointmentStatus.Booked;
    return {
      id: "busy-" + period.lineId,
      title: statusMarker + TechnicianAppointmentUtils.refLabel(period.crew) + (title ? " · " + title : ""),
      start: dayjs(period.start).toDate(),
      end: dayjs(period.end).toDate(),
      color: TechnicianAppointmentUtils.colorFor(period.crew, colorMap),
      editable: !period.locked,
      // Must be a string: refineClassName silently drops arrays and the event loses every class.
      className: [
        "tech-cal__busy",
        flagged ? "tech-cal__busy--flagged" : "",
        flagged ? "tech-cal__busy--" + String(status).toLowerCase() : ""
      ].filter(Boolean).join(" "),
      extendedProps: {
        busy: true,
        locked: period.locked,
        status,
        crew: period.crew,
        apptCode: period.appointment?.code ?? "",
        appointmentId: period.appointment?.id,
        lineId: period.lineId
      }
    } as EventInput;
  }

  static periodTitle(period: namaTypes.DTOCrewPeriod): string {
    const title = TechnicianAppointmentUtils.refLabel(period.appointment);
    const description = Translator.getName(period.arabicTitle, period.englishTitle);
    return ObjectChecker.isEmptyOrNull(description) ? title : title + ` - ( ${description} )`;
  }

  static unavailabilityEventFor(block: namaTypes.DTOCrewBlock, crew: EntityReferenceData, colorMap: Map<string, string>): EventInput {
    return {
      id: ["block", block.unavailability?.id, block.technician?.id, block.start, block.crewId].join("-"),
      title: Translator.translate("techApptUnavailable") + " · " + TechnicianAppointmentUtils.refLabel(crew),
      start: dayjs(block.start).toDate(),
      end: dayjs(block.end).toDate(),
      color: TechnicianAppointmentUtils.colorFor(crew, colorMap),
      editable: false,
      className: "tech-cal__busy tech-cal__unavailable",
      // readonly keeps the click and the drag off it; crew makes the drag guard reject it.
      extendedProps: {readonly: true, unavailable: true, documentId: block.unavailability?.id, crew, technician: block.technician}
    } as EventInput;
  }

  static crewBlockedBetween(crewId: string | undefined, start: Date, end: Date, blocks: EventInput[]): boolean {
    if (!crewId)
      return false;
    const from = start.getTime();
    const to = end.getTime();
    return blocks.some(block => {
      if ((block.extendedProps as any)?.crew?.id !== crewId || !block.start || !block.end)
        return false;
      return from < new Date(block.end as any).getTime() && to > new Date(block.start as any).getTime();
    });
  }

  static linkToAppointment(appointmentId?: string): string | undefined {
    if (!appointmentId)
      return undefined;
    return PlaceUtils.linkToPlace(
      EditPlace.createInstance(TechnicianAppointmentUtils.APPOINTMENT_ENTITY, appointmentId, ViewMode.EDIT));
  }

  static linkToUnavailability(documentId?: string): string | undefined {
    if (!documentId)
      return undefined;
    return PlaceUtils.linkToPlace(
      EditPlace.createInstance(TechnicianAppointmentUtils.UNAVAILABILITY_ENTITY, documentId, ViewMode.EDIT));
  }

  static overlapsAnotherPeriod(crewId: string | undefined, start: Date, end: Date,
                               movedEventId: string, candidates: PeriodCandidate[]): boolean {
    if (!crewId)
      return false;
    const from = start.getTime();
    const to = end.getTime();
    return candidates.some(candidate => {
      const props = candidate.extendedProps as any;
      if (candidate.id === movedEventId || props?.holiday || props?.removed
        || props?.crew?.id !== crewId)
        return false;
      if (!candidate.start || !candidate.end)
        return false;
      const otherFrom = new Date(candidate.start as any).getTime();
      const otherTo = new Date(candidate.end as any).getTime();
      return from < otherTo && to > otherFrom;
    });
  }

  private static rulesApplyingOn(date: Date, rules: namaTypes.DTOBookingRule[]): namaTypes.DTOBookingRule[] {
    const day = dayjs(date);
    return rules.filter(r => (!r.fromDate || !day.isBefore(r.fromDate, "day")) && (!r.toDate || !day.isAfter(r.toDate, "day")));
  }

  static isBookingHoliday(date: Date, rules: namaTypes.DTOBookingRule[]): boolean {
    if (!rules.length)
      return false;
    const applicable = TechnicianAppointmentUtils.rulesApplyingOn(date, rules);
    if (!applicable.length)
      return false;
    const dow = date.getDay();
    return applicable.every(r => r.days != null && !r.days[dow]?.working);
  }

  static buildHolidayEvents(rangeStart: Date | undefined, rangeEnd: Date | undefined, rules: namaTypes.DTOBookingRule[]): EventInput[] {
    if (!rangeStart || !rangeEnd)
      return [];
    const out: EventInput[] = [];
    const now = dayjs();
    const last = dayjs(rangeEnd);
    if (now.isAfter(rangeStart))
      out.push({
        id: "past",
        start: rangeStart,
        end: now.isBefore(last) ? now.toDate() : rangeEnd,
        display: "background",
        className: "tech-cal__past",
        extendedProps: {readonly: true, holiday: true}
      } as EventInput);
    if (!rules.length)
      return out;
    let d = dayjs(rangeStart).startOf("day");
    while (d.isBefore(last)) {
      if (d.add(1, "day").isAfter(now) && TechnicianAppointmentUtils.isBookingHoliday(d.toDate(), rules)) {
        const ds = d.format("YYYY-MM-DD");
        out.push({
          id: "holiday-" + ds,
          start: ds,
          end: d.add(1, "day").format("YYYY-MM-DD"),
          display: "background",
          className: "tech-cal__holiday",
          extendedProps: {readonly: true, holiday: true}
        } as EventInput);
      }
      d = d.add(1, "day");
    }
    return out;
  }

  static isSelectionAllowed(info: { start: Date; end: Date }, rules: namaTypes.DTOBookingRule[]): boolean {
    if (dayjs(info.start).isBefore(dayjs()))
      return false;
    if (!rules.length)
      return true;
    const startTime = dayjs(info.start).format("HH:mm:ss");
    let endTime = dayjs(info.end).format("HH:mm:ss");
    if (endTime === "00:00:00" && dayjs(info.end).isAfter(dayjs(info.start), "day"))
      endTime = "24:00:00";
    if (!(startTime < endTime))
      return false;

    const applicable = TechnicianAppointmentUtils.rulesApplyingOn(info.start, rules);
    if (!applicable.length)
      return false;
    const dow = info.start.getDay();
    for (const r of applicable) {
      if (!r.days)
        return true;
      const day = r.days[dow];
      if (!day || !day.working)
        continue;
      if (day.ranges?.some(rg => !!rg.start && !!rg.end
        && startTime >= TechnicianAppointmentUtils.timeOfDay(rg.start)! && endTime <= TechnicianAppointmentUtils.timeOfDay(rg.end)!))
        return true;
    }
    return false;
  }

  static buildDetailLines(events: EventInput[]): any[] {
    return events.map((ev: EventInput) => ({
      day: TechnicianAppointmentUtils.encodeDay(ev.start),
      fromTime: TechnicianAppointmentUtils.encodeTime(ev.start),
      toTime: TechnicianAppointmentUtils.encodeTime(ev.end),
      technicianCrew: (ev.extendedProps as any)?.crew as EntityReferenceData
    }));
  }
}
